import type {
  Advertisement,
  AdvertisementResponse,
  Customer,
  DealStatus,
  OfferStatus,
} from '@dobrozem/sdk';
import { AdvertisementStatus } from '@dobrozem/shared';
import clsx from 'clsx';

import { isNewStatus } from '@/utils/common';

import { Text } from '../Text';
import styles from './OfferTag.module.scss';

export type TagTypes = 'advertisement' | 'offer' | 'purchase';

const ADVERTISEMENT_STATUS_LABELS: Record<'URGENTLY', string> &
  Record<AdvertisementStatus, string> &
  Record<DealStatus, string> &
  Record<OfferStatus, string> = {
  ACCEPTED: 'Прийнята',
  ACTIVE: 'Активне',
  BLOCKED: 'Заблоковане',
  CANCELED: 'Відхилено',
  CHECKING_DOCUMENTS: 'Перевірка документів',
  DEAL_CANCELED: 'Скасована',
  DEAL_CONCLUDED: 'Угода укладена',
  DEAL_IN_PROGRESS: 'В процесі укладання угоди',
  DEAL_IS_DONE: 'Продано',
  DECLINED: 'Відхилено',
  DELETED: 'Видалено',
  DISCUSSIONS_WITH_TENANT: 'Обговорення з орендарем',
  DOCUMENTS_PROBLEM: 'Проблеми з документами',
  EXPECTING_BUYER_DOCUMENTS_CHECK: 'Очікується перевірка документів покупця',
  EXPECTING_CONTRACT_TO_BE_SIGNED_BY_BUYER: 'Очікується підписання договору покупцем',
  EXPECTING_DOCUMENTS: 'Очікуються документи',
  EXPECTING_SELLER_DOCUMENTS_CHECK: 'Очікується перевірка документів продавця',
  MODERATION_FAILED_INVALID_PROPERTY_TYPE: 'Невірне цільове призначення ділянки',
  MODERATION_FAILED_INVALID_PURPOSE: 'Невірний вид угідь',
  MODERATION_FAILED_NO_CADASTRAL_NUMBER_FOUND: 'Невірний кадастровий номер',
  MODERATION_FAILED_NO_REGISTRY_RESPONSE: 'Помилка отримання даних з регістру',
  NEW: 'Нове',
  NOTARY_SEARCH: 'Пошук нотаріуса',
  ON_MODERATION_AD_UPDATED: 'Оголошення оновлено',
  ON_MODERATION_REGISRTY_DATA_RECEIVED: 'Відредаговано',
  ON_MODERATION_REGISTRY_CHECK_IN_PROGRESS: 'Очікування даних',
  ON_MODERATION_REGISTRY_DATA_INCOMPLETE: 'Неповні дані',
  ON_MODERATION_REGISTRY_TIMEOUT: 'На модерації',
  ON_MODERATRION_REGISTRY_DATA_FAILED: 'Помилка отримання даних',
  SOLD: 'Продано',
  URGENTLY: 'Терміново',
};

const ADVERTISEMENT_STATUS_COLORS = {
  ACCEPTED: '#0B8331',
  ACTIVE: '#EAAA00',
  BLOCKED: '#000000',
  CANCELED: 'var(--yellow)',
  CHECKING_DOCUMENTS: 'var(--yellow)',
  DEAL_CANCELED: '#E5E5DC',
  DEAL_CONCLUDED: 'var(--yellow)',
  DEAL_IN_PROGRESS: '#0B8331',
  DEAL_IS_DONE: '#C4C4C4',
  DECLINED: '#000000',
  DELETED: '#C4C4C4',
  DISCUSSIONS_WITH_TENANT: 'var(--yellow)',
  DOCUMENTS_PROBLEM: 'var(--yellow)',
  EXPECTING_BUYER_DOCUMENTS_CHECK: 'var(--yellow)',
  EXPECTING_CONTRACT_TO_BE_SIGNED_BY_BUYER: 'var(--yellow)',
  EXPECTING_DOCUMENTS: 'var(--yellow)',
  EXPECTING_SELLER_DOCUMENTS_CHECK: 'var(--yellow)',
  MODERATION_FAILED_INVALID_PROPERTY_TYPE: '#FF4C2D',
  MODERATION_FAILED_INVALID_PURPOSE: '#FF4C2D',
  MODERATION_FAILED_NO_CADASTRAL_NUMBER_FOUND: '#FF4C2D',
  MODERATION_FAILED_NO_REGISTRY_RESPONSE: '#FF4C2D',
  NEW: '#EAAA00',
  NOTARY_SEARCH: 'var(--yellow)',
  ON_MODERATION_AD_UPDATED: 'var(--yellow)',
  ON_MODERATION_REGISRTY_DATA_RECEIVED: '#FF4C2D',
  ON_MODERATION_REGISTRY_CHECK_IN_PROGRESS: 'var(--yellow)',
  ON_MODERATION_REGISTRY_DATA_INCOMPLETE: '#FF4C2D',
  ON_MODERATION_REGISTRY_TIMEOUT: '#EAAA00',
  ON_MODERATRION_REGISTRY_DATA_FAILED: '#FF4C2D',
  SOLD: '#4B5057',
  URGENTLY: '#FF4C2D',
};

export const OfferTag = ({
  advertisement,
  className,
  status,
  type = 'advertisement',
  // user,
}: {
  advertisement?: Advertisement | AdvertisementResponse;
  className?: string;
  status?: OfferStatus;
  type?: TagTypes;
  user?: Customer | null;
}) => {
  const getStatus = () => {
    if (type === 'offer' && status) return status;

    if (!advertisement) return AdvertisementStatus.NEW;

    // A sold ad is never "new", even if it was created recently
    if (advertisement.status === 'SOLD') return AdvertisementStatus.SOLD;

    const isUrgently = advertisement.status === 'ACTIVE' && advertisement.sellAsap;

    const isNew = isNewStatus(advertisement.createdAt);

    return isUrgently ? 'URGENTLY' : isNew ? 'NEW' : advertisement.status;
  };

  const currentStatus = getStatus();

  const label = ADVERTISEMENT_STATUS_LABELS[currentStatus] || ADVERTISEMENT_STATUS_LABELS['NEW'];

  const backgroundColor = ADVERTISEMENT_STATUS_COLORS?.[currentStatus] || 'var(--yellow)';

  return (
    <Text
      className={clsx(styles.wrapper, className)}
      color='white'
      style={{ backgroundColor }}
      type='p5'
    >
      {label}
    </Text>
  );
};
